/**
 * Generic tool dispatch for browser tools.
 *
 * Pure function that maps a tool name + args to an executor method call.
 * No MCP formatting, no lifecycle management — just name + args → result.
 *
 * Consumers: session daemon, MCP adapter, CLI commands.
 *
 * @module
 */

import type { ElementHintCategory } from '@chatsdet/types';
import type { BrowserToolExecutor, ToolResult } from './executor.js';
import { withToolTimeout, pickToolBudget } from './timeout.js';

/**
 * Dispatch a browser tool call to the appropriate executor method.
 *
 * Returns the raw ToolResult (string or MCPContentResult).
 * Throws if the tool name is unrecognized.
 */
export async function dispatchBrowserTool(
  executor: BrowserToolExecutor,
  toolName: string,
  args: Record<string, unknown>
): Promise<ToolResult> {
  switch (toolName) {
    case 'browser_navigate': {
      const url = args.url;
      if (typeof url !== 'string') {
        throw new Error('browser_navigate requires a string url');
      }
      if (url.trim() === 'back') {
        return executor.navigateBack();
      }
      return executor.navigate(url);
    }

    case 'browser_navigate_back':
      return executor.navigateBack();

    case 'browser_snapshot':
      return executor.snapshot({
        mode: args.mode as 'auto' | 'both' | 'screenshot' | 'tree' | 'tree-refless' | undefined,
        expand: args.expand as string | undefined,
        search: args.search as string | undefined,
        showCoordinateGrid: args.showCoordinateGrid as boolean | undefined,
        probeAt: args.probeAt as { x: number; y: number } | undefined,
        hintDetail: args.hintDetail as string | undefined,
      });

    case 'browser_screenshot':
      return executor.screenshot({
        fullPage: args.fullPage as boolean | undefined,
        element: args.element as string | undefined,
        ref: args.ref as string | undefined,
        label: args.label as string | undefined,
        returnImage: args.returnImage as boolean | undefined,
      });

    case 'browser_evaluate':
      return executor.evaluate({
        expression: args.function as string,
        label: args.label as string | undefined,
        ref: args.ref as string | undefined,
      });

    case 'browser_run_code':
      return executor.runCode({
        code: args.code as string,
        observe: args.observe as 'immediate' | 'settle' | undefined,
      });

    case 'browser_scan': {
      // The dispatcher/CLI path never stamps or passes a binding — a human
      // driving a live session just wants the located control's text form.
      // `--visualize` opts into the annotated context crop (media), so a human
      // can SEE what scan matched; otherwise it stays text-only (cheap default).
      const renderVisual = args.visualize === true;
      const scanResult = await executor.scan(
        {
          query: args.query as string | undefined,
          at: args.at as { x: number; y: number } | undefined,
          targetRole: args.targetRole as string | undefined,
          targetText: args.targetText as string | undefined,
          relation: args.relation as 'self' | 'descendant' | 'row' | undefined,
          visualize: renderVisual || undefined,
        },
        renderVisual
      );
      return scanResult.media?.length ? { text: scanResult.text, media: scanResult.media } : scanResult.text;
    }

    case 'browser_console_messages':
      // `sinceMs` is a host-side scoping concern injected by the in-process
      // tool factory (browserTools.ts), not an agent-facing arg. Dispatcher
      // forwards only the agent-visible fields.
      return executor.consoleMessages({
        onlyErrors: args.onlyErrors as boolean | undefined,
        includePreStep: args.includePreStep as boolean | undefined,
      });

    case 'browser_network_requests':
      return executor.networkRequests();

    case 'browser_click':
      return executor.click({
        ref: args.ref as string | undefined,
        x: args.x as number | undefined,
        y: args.y as number | undefined,
        element: args.element as string,
        doubleClick: args.doubleClick as boolean | undefined,
        button: args.button as string | undefined,
        modifiers: args.modifiers as string[] | undefined,
      });

    case 'browser_hover':
      return executor.hover({
        ref: args.ref as string,
        element: args.element as string,
      });

    case 'browser_drag':
      return executor.drag({
        startRef: args.startRef as string | undefined,
        endRef: args.endRef as string | undefined,
        startElement: args.startElement as string,
        endElement: args.endElement as string,
        startX: args.startX as number | undefined,
        startY: args.startY as number | undefined,
        endX: args.endX as number | undefined,
        endY: args.endY as number | undefined,
      });

    case 'browser_type':
      return executor.type({
        ref: args.ref as string,
        text: args.text as string,
        element: args.element as string,
        submit: args.submit as boolean | undefined,
        delay: args.delay as number | undefined,
      });

    case 'browser_press_key':
      return executor.pressKey(args.key as string);

    case 'browser_fill_form':
      return executor.fillForm(
        args.fields as Array<{ ref: string; element?: string; value: string }>
      );

    case 'browser_select_option':
      return executor.selectOption({
        ref: args.ref as string,
        value: args.value as string,
        element: args.element as string,
      });

    case 'browser_file_upload':
      return executor.fileUpload(args.paths as string[], args.ref as string | undefined);

    case 'browser_scroll':
      return executor.scroll({
        direction: args.direction as 'up' | 'down' | 'left' | 'right',
        amount: args.amount as number | undefined,
        withinRef: args.withinRef as string | undefined,
        toRef: args.toRef as string | undefined,
        x: args.x as number | undefined,
        y: args.y as number | undefined,
      });

    case 'browser_handle_dialog':
      return executor.handleDialog({
        action: args.action as 'accept' | 'dismiss',
        promptText: args.promptText as string | undefined,
      });

    case 'browser_dismiss_overlay':
      return executor.dismissOverlay({
        preferredStrategy: args.preferredStrategy as
          | 'escape'
          | 'close_button'
          | 'backdrop'
          | 'auto'
          | undefined,
      });

    case 'browser_wait_for':
      return executor.waitFor({
        timeSec: args.time as number | undefined,
        text: args.text as string | undefined,
        textGone: args.textGone as string | undefined,
        selector: args.selector as string | undefined,
        state: args.state as string | undefined,
        timeout: args.timeout as number | undefined,
      });

    case 'browser_close':
      return executor.close();

    case 'browser_resize':
      return executor.resize(args.width as number, args.height as number);

    case 'browser_tabs':
      return executor.tabs({
        action: args.action as 'list' | 'new' | 'close' | 'select',
        index: args.index as number | undefined,
      });

    case 'browser_list_downloads':
      return executor.listDownloads();

    case 'browser_read_download':
      return executor.readDownload(args.downloadId as number | undefined);

    case 'browser_pdf_read':
      return executor.fetchPdfText(args.url as string);

    case 'browser_hint': {
      const result = await executor.hint({
        ref: args.ref as string,
        category: args.category as ElementHintCategory | undefined,
        element: args.element as string | undefined,
        frontmatter: args.frontmatter as string | undefined,
        body: args.body as string | undefined,
        invalidate: args.invalidate as boolean | undefined,
        reason: args.reason as string | undefined,
      });
      return result.message;
    }

    default:
      throw new Error(`Unknown browser tool: ${toolName}`);
  }
}

/**
 * Same as dispatchBrowserTool, but wrapped in withToolTimeout so a wedged
 * page (native dialog blocking JS, hung XHR, unresponsive context) returns a
 * sentinel-prefixed recovery string instead of hanging the caller.
 *
 * Used by the CLI session daemon so interactive sessions get the same
 * hard-timeout protection that the API agent already has via createBrowserTools.
 *
 * onTimeout/onError are optional observability hooks; the recovery path never
 * throws so callers can rely on a string-or-ToolResult return.
 */
export async function dispatchBrowserToolWithTimeout(
  executor: BrowserToolExecutor,
  toolName: string,
  args: Record<string, unknown>,
  hooks?: {
    onTimeout?: (msg: string, meta: Record<string, unknown>) => void;
    onError?: (err: unknown, meta: Record<string, unknown>) => void;
  }
): Promise<ToolResult> {
  const budgetMs = pickToolBudget(toolName, args);
  return withToolTimeout(
    toolName,
    budgetMs,
    () => dispatchBrowserTool(executor, toolName, args),
    hooks?.onTimeout,
    hooks?.onError
  );
}
